import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import useSocket from "../../hooks/useSocket";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { notify } from "../../components/ui/notify";
import {
  Layout,
  Modal,
} from "antd";

import { useDispatch, useSelector } from "react-redux";
import {
  actionAgendaPost, // ✅ CAMBIO: POST con payload completo
  actionAgendaCreate,
  actionAgendaUpdate,
  actionAgendaDelete,
  actionAgendaGetById,
} from "../../redux/actions/agenda/agenda";

import { actionCiudadesGet } from "../../redux/actions/ciudades/ciudades.js";
import { actionEstadosGet } from "../../redux/actions/estados/estados.js";
import { actionAutoridadesAllGet } from "../../redux/actions/autoridades/autoridades.js";
import OutlookRibbon from "../../components/calendar/OutlookRibbon";
import OutlookDateBar from "../../components/calendar/OutlookDateBar";
import OutlookCalendarBoard from "../../components/calendar/OutlookCalendarBoard";
import EventModal from "../../components/calendar/EventModal";
import EventViewModal from "../../components/calendar/EventViewModal";
import PrintModal from "../../components/calendar/PrintModal";
import AgendaSearchModal from "../../components/calendar/AgendaSearchModal";

import {
  buildMonthMatrix,
  buildWeekDays,
  buildDayHours,
  normalizeEvent,
} from "../../components/calendar/calendarUtils";

import "../../components/calendar/css/index.css";

dayjs.locale("es");

const { Content } = Layout;
const toLocalISO = (d) => dayjs(d).format("YYYY-MM-DDTHH:mm:ss");

const getRangeParams = (view, cursorDate) => {
  if (view === "month") {
    const from = cursorDate.startOf("month").startOf("week");
    const to = cursorDate.endOf("month").endOf("week");
    return { from: toLocalISO(from), to: toLocalISO(to) };
  }

  if (view === "day") {
    const from = cursorDate.startOf("day");
    const to = cursorDate.endOf("day");
    return { from: toLocalISO(from), to: toLocalISO(to) };
  }

  const from = cursorDate.startOf("week");
  const to = cursorDate.endOf("week");
  return { from: toLocalISO(from), to: toLocalISO(to) };
};


// Ultra-robusto: acepta array directo o payloads con items/data/items
const coerceItems = (slice) => {
  if (!slice) return [];
  if (Array.isArray(slice)) return slice;

  // casos típicos redux
  if (Array.isArray(slice.items)) return slice.items;
  if (Array.isArray(slice.data)) return slice.data;
  if (slice.data && Array.isArray(slice.data.items)) return slice.data.items;

  // axios: { data: { items } }
  if (slice.payload && Array.isArray(slice.payload.items)) return slice.payload.items;

  return [];
};

// ✅ NUEVO: IDs de ciudad (los que me diste)
const CITY_IDS = {
  mexicali: 700,
  tijuana: 703,
  penasco: 2633,
  sanluis: 2641,
  ensenada: 699,
};

// ✅ NUEVO: construir payload POST completo
const buildAgendaPayload = ({ rangeParams, filters }) => {
  const eventTypeIds = Array.isArray(filters?.eventTypeIds) ? filters.eventTypeIds : [];

  // Siempre traer todas las ciudades — el filtro de ciudad se aplica en el cliente
  // para que los conteos de los chips nunca cambien al cambiar de ciudad activa
  const autoridadIds = Array.isArray(filters?.autoridadIds) ? filters.autoridadIds : [];

  return {
    from: rangeParams?.from || null,
    to: rangeParams?.to || null,

    status: null,
    include_inactive: false,

    city_ids: [CITY_IDS.mexicali, CITY_IDS.sanluis, CITY_IDS.tijuana, CITY_IDS.penasco, CITY_IDS.ensenada],
    include_other_cities: true,

    event_type_ids: eventTypeIds,
    autoridad_ids: autoridadIds,
  };
};

// Strip "Pago de convenio (NAME vs COMPANY) AMOUNT" → "NAME VS COMPANY"
const normalizeTitleForDisplay = (title) => {
  if (!title) return title;
  const m = title.match(/^Pago de convenio\s*\((.+?)\)\s*[\d,.$\s]*$/i);
  if (m) return m[1].toUpperCase();
  // Also handle without trailing amount
  const m2 = title.match(/^Pago de convenio\s*\((.+?)\)$/i);
  if (m2) return m2[1].toUpperCase();
  return title;
};

// backend -> UI event
const mapAgendaItemToUiEvent = (it) => {
  const canceled =
    it?.status === "canceled" || it?.canceled === true || it?.is_canceled === true;

  const id = it?.id ?? it?.id_agenda ?? null;
  const start = it?.start ?? it?.start_at ?? null;
  const end = it?.end ?? it?.end_at ?? null;
  const allDay = !!(it?.allDay ?? it?.all_day);

  const ciudad_id = it?.ciudad_id ?? it?.id_ciudad ?? null;
  const nombre_ciudad = it?.nombre_ciudad || "";
  const url = it?.url ?? it?.path ?? it?.link ?? null;

  const reminder = it?.reminder || "15m";
  const inPerson = !!(it?.inPerson ?? it?.in_person);

  const recurrence =
    it?.recurrence ??
    it?.recurrence_rule ??
    it?.recurrenceRule ??
    null;

  // ✅ NUEVO: documento (viene del backend en snake_case)
  const documento_url = it?.documento_url ?? null;
  const documento_filename = it?.documento_filename ?? null;

  // Si no hay ubicación (o quedó "N/A"), usar el nombre de la autoridad
  // resuelta por el backend vía join agenda -> conciliacion/tribunal -> autoridades.
  const rawLocation = (it?.location || "").trim();
  const nombreAutoridad = it?.nombre_autoridad || "";
  const location =
    (!rawLocation || rawLocation.toUpperCase() === "N/A") && nombreAutoridad
      ? nombreAutoridad
      : it?.location || "";

  const base = normalizeEvent({
    id,
    title: normalizeTitleForDisplay(it?.title || ""),
    start,
    end,
    allDay,

    calendarId: "cal_main",
    showAs: "busy",
    recurring: !!(it?.is_recurring ?? recurrence),
    canceled,

    location,
    description: it?.description || "",
  });

  // Número de expediente real (join agenda -> tribunal.numero_expediente), no el que haya
  // quedado congelado (y a veces incorrecto, ej. folios UAC/CI viejos) en la descripción.
  let expediente = null;
  if ((it?.source_table || "").toLowerCase().includes("tribunal")) {
    expediente = it?.numero_expediente ? String(it.numero_expediente).trim() : null;
  }

  return {
    ...base,

    ciudad_id,
    nombre_ciudad,
    reminder,
    inPerson,
    recurrence,
    url,

    // ✅ REINYECTA documento para EventViewModal
    documento_url,
    documento_filename,

    color_hex: it?.color_hex || null,
    color: it?.color_hex || null,
    id_agenda_evento: it?.id_agenda_evento ?? it?.id_evento ?? null,
    source_table: it?.source_table ?? null,

    is_recurring: it?.is_recurring ?? null,
    expediente,

    // Razón social vigente (join agenda -> tribunal -> tribunal_empresas), no la que haya
    // quedado congelada en la descripción del evento al crearse.
    razon_social: it?.nombre_razon_social || "",

    // Nombre de la parte actora vigente (join agenda -> tribunal.nombre_parte_actora).
    // Necesario porque algunos eventos viejos (pago/parcialidad de convenio) no traen
    // "Actora: ..." en la descripción, así que no hay de dónde reconstruir el nombre.
    actora: it?.nombre_actora || "",
  };
};

export default function OutlookCalendarPage() {
  const dispatch = useDispatch();

  const [view, setView] = useState("day");
  const [cursorDate, setCursorDate] = useState(dayjs());
  const [selectedCalendars, setSelectedCalendars] = useState({ cal_main: true });

  const lastNotifAtRef = useRef(0);
  const pendingNotifRef = useRef(null);
  const notifyAgendaUpdate = useCallback((text) => {
    const now = Date.now();
    const cooldownMs = 1500;

    
    if (now - lastNotifAtRef.current < cooldownMs) {
      if (pendingNotifRef.current) clearTimeout(pendingNotifRef.current);
      pendingNotifRef.current = setTimeout(() => {
        lastNotifAtRef.current = Date.now();
        notify.info({
          message: "Agenda actualizada",
          description: text || "Se detectaron cambios en la agenda.",
          key: "agenda_update",
        });
        pendingNotifRef.current = null;
      }, cooldownMs);
      return;
    }

    lastNotifAtRef.current = now;
    notify.info({
      message: "Agenda actualizada",
      description: text || "Se detectaron cambios en la agenda.",
      key: "agenda_update",
    });
  }, []);

  // ✅ CAMBIO: filtros nuevos (OutlookRibbon los inicializa a "todos")
  const [filters, setFilters] = useState({
    colorTags: null,
    eventTypeIds: null,
  });

  const rangeParams = useMemo(() => getRangeParams(view, cursorDate), [view, cursorDate]);


  const rangeRef = useRef(rangeParams);

  // guardar el último payload para refrescos en tiempo real
  const lastPayloadRef = useRef(null);

  useEffect(() => {
    rangeRef.current = rangeParams;
  }, [rangeParams]);

  // Handler de eventos Socket.IO: refresca agenda al recibir AGENDA_INVALIDATE
  const handleSocketEvent = useCallback((msg) => {
    if (msg?.type === "AGENDA_INVALIDATE" || msg?.type === "AGENDA_REFRESH_RANGE") {
      notifyAgendaUpdate("Hubo una actualización. Refrescando vista…");

      const baseRange = rangeRef.current;
      const payload = lastPayloadRef.current
        ? { ...lastPayloadRef.current, from: baseRange.from, to: baseRange.to }
        : null;

      if (payload) dispatch(actionAgendaPost(payload));
    }
  }, [dispatch, notifyAgendaUpdate]);

  useSocket("event", handleSocketEvent);

  // ===== AGENDA: LEE BIEN items =====
  const agendaSlice = useSelector((state) => state.agenda || {});
  const agendaItems = useMemo(
    () => coerceItems(agendaSlice?.data ?? agendaSlice),
    [agendaSlice]
  );

  // ===== CIUDADES =====
  const ciudadesSlice = useSelector((state) => state.ciudades || {});
  const ciudadesItems = useMemo(() => coerceItems(ciudadesSlice), [ciudadesSlice]);

const ciudadOptions = useMemo(() => {
  return ciudadesItems
    .map((c) => ({
      label: c?.nombre || c?.code || `Ciudad ${c?.id}`,
      value: c?.id,
      id_estado: c?.id_estado, // 🔥 CLAVE PARA FILTRAR
    }))
    .filter((o) => o.label && o.value != null && o.id_estado != null);
}, [ciudadesItems]);

const estadosSlice = useSelector((state) => state.estados || {});
  const estadosItems = useMemo(() => coerceItems(estadosSlice), [estadosSlice]);

  const estadoOptions = useMemo(() => {
    return estadosItems.map((c) => ({
      label: c.nombre || c.code || `estado ${c.id}`,
      value: c.id,
    }));
  }, [estadosItems]);

  // ===== AUTORIDADES =====
  const autoridadesSlice = useSelector((state) => state.autoridades || {});
  const autoridadesItems = useMemo(() => coerceItems(autoridadesSlice), [autoridadesSlice]);

  const autoridadOptions = useMemo(() => {
    return autoridadesItems
      .map((a) => ({
        label: a?.nombre || `Autoridad ${a?.id}`,
        value: a?.id,
      }))
      .filter((o) => o.label && o.value != null);
  }, [autoridadesItems]);
  const agendaById = useMemo(() => {
    const m = new Map();
    (agendaItems || []).forEach((it) => {
      const id = it.id ?? it.id_agenda;
      if (id != null) m.set(id, it);
    });
    return m;
  }, [agendaItems]);

  const [draft, setDraft] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // ===== NUEVO: modal de VISTA (click) =====
  const [viewOpen, setViewOpen] = useState(false);
  const [viewEvent, setViewEvent] = useState(null);


 

  useEffect(() => {
    dispatch(actionCiudadesGet({}));
    dispatch(actionEstadosGet({}));
    dispatch(actionAutoridadesAllGet());
  }, [dispatch]);

  const eventsUi = useMemo(() => {
    const agendaEvents = (agendaItems || []).map(mapAgendaItemToUiEvent);

    return [...agendaEvents];
  }, [agendaItems]);

  const withinRange = (ev, fromISO, toISO) => {
    const from = dayjs(fromISO);
    const to = dayjs(toISO);
    const start = dayjs(ev.start);
    const end = dayjs(ev.end || ev.start);

    return start.isBefore(to) && end.isAfter(from);
  };

  const eventsUiInRange = useMemo(() => {
    return (eventsUi || []).filter((ev) =>
      withinRange(ev, rangeParams.from, rangeParams.to)
    );
  }, [eventsUi, rangeParams]);

  const visibleEvents = useMemo(() => {
    const enabledIds = Object.keys(selectedCalendars).filter(
      (k) => selectedCalendars[k]
    );

    return eventsUiInRange.filter((e) =>
      enabledIds.includes(e.calendarId)
    );
  }, [eventsUiInRange, selectedCalendars]);

  const rangeLabel = useMemo(() => {
    if (view === "month") return cursorDate.format("MMMM YYYY");
    if (view === "day") return cursorDate.format("D MMMM, YYYY");

    const weekDays = buildWeekDays(cursorDate, view === "week_work");
    const start = weekDays[0];
    const end = weekDays[weekDays.length - 1];
    return `${start.format("DD")}–${end.format("DD")} de ${start.format(
      "MMMM"
    )} de ${start.format("YYYY")}`;
  }, [cursorDate, view]);

  const onToday = () => setCursorDate(dayjs());

  const onPrev = () => {
    if (view === "day") setCursorDate((d) => d.subtract(1, "day"));
    else if (view === "month") setCursorDate((d) => d.subtract(1, "month"));
    else setCursorDate((d) => d.subtract(1, "week"));
  };

  const onNext = () => {
    if (view === "day") setCursorDate((d) => d.add(1, "day"));
    else if (view === "month") setCursorDate((d) => d.add(1, "month"));
    else setCursorDate((d) => d.add(1, "week"));
  };

  const onPickMonthYear = (monthDayjs) => {
    const currentDay = cursorDate.date();
    const target = monthDayjs.date(1);
    const daysInTarget = target.daysInMonth();
    const safeDay = Math.min(currentDay, daysInTarget);
    setCursorDate(target.date(safeDay));
  };

  const openNewEvent = (base) => {
    setDraft({
      id: null,
      title: "",
      start: base?.start || cursorDate.hour(8).minute(0).second(0).toISOString(),
      end: base?.end || cursorDate.hour(8).minute(30).second(0).toISOString(),
      allDay: false,

      ciudad_id: null,
      location: "",

      calendarId: "cal_main",
      showAs: "busy",
      recurring: false,
      canceled: false,
      description: "",
      reminder: "15m",
      inPerson: false,

      id_agenda_evento: null,
      source_table: null,
      source_id: null,

      recurrence: null,
    });

    setModalOpen(true);
  };

  const onSelectSlot = ({ start, end }) => openNewEvent({ start, end });

const onSaveEvent = async (payload, files = []) => {
  try {
    if (!payload) throw new Error("payload vacío");

    const isUpdate = !!draft?.id;

    const filesMap =
      Array.isArray(files) && files.length
        ? { documento: files[0] } // 👈 igual que desvinculaciones: filesMap.documento
        : null;

    if (isUpdate) {
      await dispatch(actionAgendaUpdate(draft.id, payload, rangeParams, () => {}, filesMap));
    } else {
      await dispatch(actionAgendaCreate(payload, rangeParams, () => {}, filesMap));
    }

    setModalOpen(false);
    setDraft(null);

    notify.info({
      message: "Evento guardado",
      description: "",
      key: "agenda_creado",
    });
  } catch (e) {
    const msg =
      e?.response?.data?.detail || e?.message || "No se pudo guardar el evento";
    notify.error(msg);
  }
};

  // ===== CLICK = VISTA =====
  const onViewEvent = (ev) => {
      console.log("VIEW EVENT =>", ev); // debe traer documento_url

    setViewEvent(ev);
    setViewOpen(true);
  };

  // ===== DOBLE CLICK / EDITAR =====
  const onEditEvent = async (ev) => {
    const id = ev?.id ?? ev?.id_agenda;
    const full = await dispatch(actionAgendaGetById(id)); // que retorne el objeto completo
    setDraft(full);
    setModalOpen(true);
  };

  const onDeleteEvent = async (data) => {
    try {
      await dispatch(actionAgendaDelete(data.id, rangeParams));
      notify.info({
      message: "Evento eliminado",
      description:  "",
      key: "agenda_elimnado",
    });
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e?.message || "No se pudo eliminar el evento";
        notify.info({
      message: msg,
      description:  "",
      key: "agenda_no_elimnado",
    });

    }
  };

  const onSave = async (payload, files) => {
  await onSaveEvent(payload, files);
};

  // ===== BUSCAR AGENDA: navegar a la fecha sin abrir modal =====
  const onSelectSearchEvent = (rawItem) => {
    const rawStart = rawItem?.start_at || rawItem?.start || null;
    const startDate = rawStart ? dayjs(rawStart) : null;
    if (startDate && startDate.isValid()) {
      setCursorDate(startDate);
    }
    setSearchOpen(false);
  };

  const boardModel = useMemo(() => {
    if (view === "month") return { kind: "month", matrix: buildMonthMatrix(cursorDate) };
    if (view === "day") {
      return { kind: "day", day: cursorDate.startOf("day"), hours: buildDayHours() };
    }
    return {
      kind: "week",
      days: buildWeekDays(cursorDate, view === "week_work"),
      hours: buildDayHours(),
      workWeek: view === "week_work",
    };
  }, [cursorDate, view]);
  useEffect(() => {
  if (!Array.isArray(filters?.colorTags)) return;
  if (!Array.isArray(filters?.eventTypeIds)) return;
  const payload = buildAgendaPayload({ rangeParams, filters });
  lastPayloadRef.current = payload;
  dispatch(actionAgendaPost(payload));
}, [dispatch, rangeParams, filters]);



  return (
    <Layout className="ol-root">
      <div className="ol-ribbonWrap">
        <OutlookRibbon
          view={view}
          setView={setView}
          filters={filters}
          setFilters={setFilters}
          autoridadOptions={autoridadOptions}
          onCreateEvent={() => openNewEvent()}
          onOpenPrint={() => setPrintOpen(true)}
          onOpenSearch={() => setSearchOpen(true)}
        />
      </div>

      <Layout className="ol-shell">
        <Layout className="ol-main">
          <Content className="ol-content">
            <OutlookCalendarBoard
              model={{
                ...boardModel,
                anchor: cursorDate,
                view: view === "week_work" ? "workweek" : view,
              }}
              cursorDate={cursorDate}
              view={view}
              events={visibleEvents}
              onSelectSlot={onSelectSlot}
              onViewEvent={onViewEvent}
              onEditEvent={onEditEvent}
              onDeleteEvent={onDeleteEvent}
              onPrev={onPrev}
              onNext={onNext}
              onPickDate={setCursorDate}
              filters={filters}
              setFilters={setFilters}
            />
          </Content>
        </Layout>
      </Layout>

      <EventModal
        open={modalOpen}
        draft={draft}
        ciudadOptions={ciudadOptions}
        estadoOptions={estadoOptions}
        events={visibleEvents}
        onCancel={() => {
          setModalOpen(false);
          setDraft(null);
        }}
        onSave={onSave}
      />

      <PrintModal
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        view={view}
        cursorDate={cursorDate}
        events={visibleEvents}
      />

      <AgendaSearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelectEvent={onSelectSearchEvent}
      />

      <EventViewModal
        open={viewOpen}
        event={viewEvent}
        onClose={() => {
          setViewOpen(false);
          setViewEvent(null);
        }}
        onEdit={(ev) => {
          setViewOpen(false);
          setViewEvent(null);
          onEditEvent(ev);
        }}
        onDelete={(ev) => {
          Modal.confirm({
            title: "Eliminar evento",
            content: `Se eliminará: "${ev?.title || ""}"`,
            okText: "Eliminar",
            okButtonProps: { danger: true },
            cancelText: "Cancelar",
            onOk: async () => {
              setViewOpen(false);
              setViewEvent(null);
              await onDeleteEvent(ev.id);
            },
          });
        }}
      />
    </Layout>
  );
}
